import { Form, Button, Spinner } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";

import { SiteLogo } from "src/assets";
import { useForgotPassword } from "src/hooks/useAuth";
import type { ForgotPasswordFormType } from "src/types/forgotPassword";
import { ForgotPasswordSchema } from "src/validation/forgotPassword.schema";

export default function ForgotPassword() {
  const { mutate: forgotPassword, isPending } = useForgotPassword();

  const { control, handleSubmit, reset } = useForm<ForgotPasswordFormType>({
    mode: "onBlur",
    resolver: zodResolver(ForgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = (values: ForgotPasswordFormType) => {
    forgotPassword(values, { onSuccess: () => reset() });
  };

  return (
    <div className="login-page d-flex flex-fill">
      <div className="col-form">
        <div className="logo d-flex flex-column justify-content-center flex-fill mb-3">
          <img src={SiteLogo} alt="Site logo" />
        </div>
        <div className="d-flex flex-column justify-content-center flex-fill">
          <h3 className="mb-2 text-purple">Forgot Your Password?</h3>
          <p className="mb-3">
            Enter your email to get instructions to reset password
          </p>
        </div>


        <Form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="email"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Form.Group className="mb-4" controlId="formEmail">
                <Form.Label className="float-start mb-2">
                  Email address *
                </Form.Label>
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="bi bi-envelope"></i>
                  </span>
                  <Form.Control
                    {...field}
                    type="email"
                    // placeholder="Email"
                    disabled={isPending}
                    isInvalid={!!(error && error.message)}
                  />
                </div>
                {error && error.message && (
                  <div className="text-danger small mt-1">
                    {error.message}
                  </div>
                )}
              </Form.Group>
            )}
          />

          <div className="my-5">
            <Button
              className="w-100"
              variant="purple"
              type="submit"
              disabled={isPending}
              style={{ minHeight: "45px" }}
            >
              {isPending && (
                <Spinner
                  className="me-1"
                  animation="border"
                  size="sm"
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              )}
              Send me reset password instructions
            </Button>
            <div className="pt-5">
              <Link to="/users/sign_in" className="d-flex align-items-center">
                <i className="bi bi-arrow-left-circle me-2"></i>
                Back to Sign in
              </Link>
            </div>
          </div>
        </Form>
        <p className="small text-center text-primary m-0 d-flex flex-column align-items-center justify-content-center flex-fill">
          © 2026 Foresight Elusa Health All rights reserved
        </p>
      </div>
      <div className="col-bg d-none d-lg-flex">
        <div className="bg-text">
          <h2 className="mb-2">
            Smarter Insights.
            <br />
            Better Patient Outcomes.
          </h2>
          <p className="mb-2">
            Foresight transforms patient services data into actionable
            intelligence, empowering pharmaceutical manufacturers to optimize
            patient access, enhance program performance, and improve patient
            outcomes.
          </p>
        </div>
      </div>
    </div>
  );
}
